import type { Zahlung } from '../../api';
import { formatiereEuro } from '../../warenkorb';
import {
  kartenSchritte,
  kartenTasten,
  kartenZustand,
  type KartenSchritt,
  type KartenZustand,
} from './logik';
import { ZahlungsHuelle } from './ZahlungsHuelle';

const UEBERSCHRIFT: Record<KartenZustand, string> = {
  warten: 'Verbindung zum Terminal …',
  terminal: 'Kunde zahlt am Terminal',
  unklar: 'Ergebnis unklar – Zahlung bleibt offen',
  unterbrochen: 'Zahlungsstand derzeit nicht abrufbar',
  meldung: 'Hinweis zur Zahlung',
  bezahlt: 'Zahlung erfolgreich',
  abgelehnt: 'Zahlung abgelehnt',
  abgebrochen: 'Zahlung abgebrochen',
  erstattet: 'Betrag erstattet',
};

const ERKLAERUNG: Record<KartenZustand, string> = {
  warten: 'Die Zahlung wird an das Terminal gesendet. Nicht erneut starten.',
  terminal: 'Die Zahlung läuft am Terminal. Nicht erneut starten.',
  unklar:
    'Nicht erneut starten. Ergebnis am Terminal prüfen; die Kasse merkt sich die Zahlung und klärt sie über „Zahlung prüfen“.',
  unterbrochen: 'Die Zahlung bleibt offen und wird nicht neu gestartet.',
  meldung: 'Die Zahlung bleibt offen, solange das Terminal kein Ergebnis meldet.',
  bezahlt: 'Der Beleg wird jetzt erstellt.',
  abgelehnt: 'Es wurde kein Beleg erstellt.',
  abgebrochen: 'Es wurde kein Beleg erstellt.',
  erstattet: 'Die Zahlung ist erstattet.',
};

/**
 * Kartenzahlungsdialog V2: reine Darstellung über dem bestehenden Ablauf in `ZahlungDialog`
 * (Abfrage des Stands, Abbruch, ZVT-Klärung, „Dialog schließen“). Bedingungen und Aufrufe
 * der Tasten sind dieselben wie im bisherigen Dialog; es gibt hier keine Taste, die eine
 * Zahlung startet – bei unklarem Ergebnis ist ein zweiter Start damit ausgeschlossen.
 */
export function KartenDialogV2(props: {
  stand: Zahlung;
  terminalText: string | null;
  klaerungsgrund: string | null;
  klaerbar: boolean;
  meldung: string | null;
  unterbrechung: string | null;
  stornoLaeuft: boolean;
  onStornieren: () => void;
  onKlaeren: (ergebnis: 'bezahlt' | 'nicht_bezahlt') => void;
  onSchliessen: () => void;
}) {
  const { stand } = props;
  const zustand = kartenZustand({
    status: stand.status,
    terminalText: props.terminalText,
    klaerungsgrund: props.klaerungsgrund,
    klaerbar: props.klaerbar,
    meldung: props.meldung,
    unterbrechung: props.unterbrechung,
  });
  const regel = kartenTasten(zustand);
  const ausstehend = stand.status === 'ausstehend';
  const anbieter = stand.anbieter === 'zvt' ? 'Terminal im Laden' : stand.anbieter;
  const ton =
    zustand === 'abgelehnt' || zustand === 'abgebrochen'
      ? 'gefahr'
      : zustand === 'bezahlt' || zustand === 'erstattet'
        ? 'erfolg'
        : zustand === 'unklar' || zustand === 'unterbrochen' || zustand === 'meldung'
          ? 'warnung'
          : 'marke';

  return (
    <ZahlungsHuelle
      art="Kartenzahlung"
      betragLabel="Betrag Karte"
      betragCent={stand.betragCent}
      info={`${anbieter} · Ref. ${stand.zahlungsreferenz}`}
      regel={regel}
      beschaeftigt={ausstehend || props.stornoLaeuft}
      klasse="zv2-karte"
      fokusSchluessel="karte"
      fuss={
        <>
          <button
            type="button"
            className="taste gefahr zv2-abbruch"
            disabled={props.stornoLaeuft || !ausstehend}
            onClick={props.onStornieren}
          >
            Zahlung abbrechen
          </button>
          {props.klaerbar && props.klaerungsgrund === null && (
            <button
              type="button"
              className="taste zweit"
              disabled={props.stornoLaeuft}
              title="Terminalbeleg zeigt eine erfolgreiche Zahlung über diesen Betrag"
              onClick={() => {
                props.onKlaeren('bezahlt');
              }}
            >
              Terminal: bezahlt
            </button>
          )}
          {props.klaerbar && (
            <button
              type="button"
              className="taste zweit"
              disabled={props.stornoLaeuft}
              title="Am Terminal keine Zahlung gebucht bzw. dort storniert"
              onClick={() => {
                props.onKlaeren('nicht_bezahlt');
              }}
            >
              Terminal: nicht bezahlt
            </button>
          )}
          {(props.meldung !== null || props.unterbrechung !== null) && ausstehend && (
            <button
              type="button"
              className="taste zweit"
              title="Das Ergebnis ist unbekannt – die Kasse merkt sich die Zahlung und prüft sie später"
              onClick={props.onSchliessen}
            >
              Dialog schließen – Zahlung bleibt offen
            </button>
          )}
        </>
      }
    >
      <div className="zv2-links">
        <div className="zv2-karten-kopf">
          <span className={`zv2-symbol ${ton}`} aria-hidden="true">
            ▭
          </span>
          <h3 role="status" aria-live="polite">
            {UEBERSCHRIFT[zustand]}
          </h3>
        </div>
        <p className="zv2-text">{ERKLAERUNG[zustand]}</p>
        {stand.trinkgeldCent !== undefined && stand.trinkgeldCent > 0 && (
          <p className="zv2-text hinweis">
            davon Trinkgeld {formatiereEuro(stand.trinkgeldCent)} € (kein Umsatz)
          </p>
        )}
        <p className="zv2-text hinweis">
          Stand: <strong>{stand.status}</strong>
          {props.terminalText !== null && ` – ${props.terminalText}`}
        </p>
        {props.klaerungsgrund !== null && (
          <div className="zv2-meldung warnung" role="alert">
            <strong>Kein Beleg, kein neuer Start</strong>
            <span>
              Ergebnis unklar: {props.klaerungsgrund}. Die Karte kann belastet sein – am Terminal
              prüfen und dort stornieren, nicht erneut kassieren.
            </span>
          </div>
        )}
        {props.klaerbar && props.klaerungsgrund === null && (
          <div className="zv2-meldung warnung" role="alert">
            <strong>Kein Beleg, kein neuer Start</strong>
            <span>
              Seit 10 Minuten kein Ergebnis. Ergebnis am Terminal ablesen und mit Namen bestätigen –
              nicht erneut kassieren.
            </span>
          </div>
        )}
        {props.meldung !== null && (
          <div className="zv2-meldung warnung" role="alert">
            <span>{props.meldung}</span>
          </div>
        )}
        {props.unterbrechung !== null && ausstehend && (
          <div className="zv2-meldung warnung" role="status">
            <span>
              Zahlungsstand derzeit nicht abrufbar: {props.unterbrechung} Die Zahlung bleibt offen
              und wird nicht neu gestartet – der Stand wird automatisch wieder abgefragt.
            </span>
          </div>
        )}
      </div>
      <KartenAblauf schritte={kartenSchritte(zustand)} />
    </ZahlungsHuelle>
  );
}

/** Ablauf rechts im Kartendialog (Start → Terminal → Bezahlt → Beleg); auch für den Kartenstatus. */
export function KartenAblauf(props: { schritte: readonly KartenSchritt[] }) {
  return (
    <div className="zv2-block zv2-ablauf">
      <span className="zv2-ablauf-titel">Ablauf</span>
      <ol>
        {props.schritte.map((s, i) => (
          <li key={s.name} className={`zv2-schritt ${s.stand}`}>
            <span className="zv2-schritt-marke" aria-hidden="true">
              {s.stand === 'erledigt' ? '✓' : s.stand === 'fehler' ? '!' : String(i + 1)}
            </span>
            <span>
              {s.name}
              <span className="unsichtbar">
                {s.stand === 'erledigt'
                  ? ' (erledigt)'
                  : s.stand === 'aktiv'
                    ? ' (läuft)'
                    : s.stand === 'warnung'
                      ? ' (unklar)'
                      : s.stand === 'fehler'
                        ? ' (nicht erfolgt)'
                        : ' (offen)'}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
